import { Injectable, inject } from '@angular/core';

import { LAST_ACTIVITY_KEY } from '@core/auth/session-timeout.service';
import { UserSessionService } from '@core/auth/user-session.service';
import { LoginModel } from '@core/models/application.models';
import { ActiveStoreService } from '@core/services/active-store.service';
import { AppStorageService } from '@core/services/app-storage.service';

/**
 * One release only: carries a signed-in user's state from the previous release's storage keys into the `pos.*` keys,
 * so someone working at deploy time keeps their session. The old keys are removed once read, so it runs once per
 * browser. Remove this file, its provider in app.config.ts and its spec one week after the storage rework reaches
 * production: by then every machine has loaded the new release. See docs/guidelines/storage-guidelines.md §1.
 */
@Injectable({ providedIn: 'root' })
export class LegacyStorageBridge {
  private storage = inject(AppStorageService);
  private userSession = inject(UserSessionService);
  private activeStore = inject(ActiveStoreService);

  run(): void {
    this.bridgeUser();
    this.bridgeStore();
    // A server cache: the layout fetches the lookups again, so the old key is only dropped.
    this.storage.remove('local', 'enums');
    this.bridgeSmallKeys();
  }

  private bridgeUser(): void {
    const user = this.storage.get<Partial<LoginModel> | null>('local', 'user', null);
    if (user && typeof user === 'object' && !Array.isArray(user) && !this.userSession.isSignedIn()) {
      this.userSession.save(user);
    }
    // The timeout is read from the user now; this was a copy of it.
    this.storage.remove('local', 'user');
    this.storage.remove('local', 'sessionTimeoutMinutes');
  }

  private bridgeSmallKeys(): void {
    const lastActivity = Number(this.storage.get<unknown>('local', 'lastActivityTime', 0));
    if (lastActivity > 0) {
      this.storage.set('local', LAST_ACTIVITY_KEY, lastActivity);
    }
    if (this.storage.get<unknown>('session', 'pageReloaded', null) === true) {
      this.storage.set('session', 'pos.session.pageReloaded', true);
    }
    this.storage.remove('local', 'lastActivityTime');
    this.storage.remove('session', 'pageReloaded');
    // A UI preference and a server cache: dropped, not carried over.
    this.storage.remove('local', 'selectedTab');
    this.storage.remove('local', 'qz-certificate');
    this.storage.remove('local', 'qz-certificate-version');
  }

  /** outletID, zipCode and pos_cardIntegrated fold into one value, stamped with the bridged user's id. */
  private bridgeStore(): void {
    const storeId = Number(this.storage.get<unknown>('local', 'outletID', 0));
    if (storeId > 0 && this.userSession.isSignedIn()) {
      this.activeStore.select(storeId);
      this.activeStore.setZipCode(this.storage.get<string | null>('local', 'zipCode', null));
      // The previous release wrote the flag as a bare string; JSON-parsing 'true' gives true.
      this.activeStore.setCardIntegrated(this.storage.get<unknown>('local', 'pos_cardIntegrated', false) === true);
    }
    this.storage.remove('local', 'outletID');
    this.storage.remove('local', 'zipCode');
    this.storage.remove('local', 'pos_cardIntegrated');
  }
}
